import React, { useState } from "react";
import { Button, Form, Row, Col } from "react-bootstrap";
import CustomSelect from "components/custom-select/CustomSelect";
import IconButton from "components/icon-button";
import Checkbox from "components/checkbox/Checkbox";
import ConfirmModal from "components/confirm-modal";
import { ReactComponent as OutlinePlus16 } from "assets/svg/outline-plus-16.svg";
import { ReactComponent as OutlineCross } from "assets/svg/outline-cross.svg";
import {
  NON_PHYSICIAN_DEGREE_OPTIONS,
  UNDERGRADUATE_DEGREES,
  YEAR_OPTIONS,
  PHYSICIAN_PRIMARY_SPECIALTY,
  PSYCHIATRY_SUBSPECIALTY_OPTIONS,
} from "../utility/constants";
import getOrchidProTypeLabel from "../utility/getOrchidProTypeLabel";
import getOrchidProTypeOrder from "../utility/getOrchidProTypeOrder";
import getClasses from "utils/getClasses";
import styles from "./styles.module.scss";
import {
  getItemsByOrchidProType,
  getItemsNotCurrOrchidProType,
} from "../utility/getItemsByOrchidProType";
import { checkIsTherapist } from "../utility/checkOrchidProType";
import messages from "messages";
import { track } from "utils/amplitude";

const getFinalDegreesArr = (degreeType, degreesArr, orchidProTypeId) => {
  return degreesArr.length === 0
    ? [
        {
          type: degreeType,
          conferring_entity: null,
          degree: null,
          year: null,
          orchid_pro_type_id: orchidProTypeId,
        },
      ]
    : degreesArr;
};

const getFinalTrainingArr = (trainingType, trainingArr, orchidProTypeId) => {
  return trainingArr.length === 0
    ? [
        {
          type: trainingType,
          conferring_entity: null,
          program: null,
          specialty: null,
          subspecialty: null,
          year: null,
          orchid_pro_type_id: orchidProTypeId,
        },
      ]
    : trainingArr;
};

const EducationTraining = ({
  fieldChangedCallback,
  registration,
  data,
  amplitudePageName,
}) => {
  const [undergradIndexToDelete, setUndergradIndexToDelete] =
    useState(undefined);
  const [graduateIndexToDelete, setGraduateIndexToDelete] = useState(undefined);

  const onDegreeInputChange =
    (degreeType, orchidProTypeId, index) => (event) => {
      const degreesArr = getItemsByOrchidProType(
        data[degreeType],
        orchidProTypeId,
      );
      const remained = getItemsNotCurrOrchidProType(
        data[degreeType],
        orchidProTypeId,
      );

      const degrees = getFinalDegreesArr(
        degreeType,
        degreesArr,
        orchidProTypeId,
      );
      const inputField = event.currentTarget;

      degrees[index] = {
        ...degrees[index],
        [inputField.name]: inputField.value,
      };

      fieldChangedCallback(degreeType, remained.concat(degrees));
    };

  const onTrainingInputChange = (trainingType, orchidProTypeId) => (event) => {
    const type =
      trainingType === "fellowships_json" ? "fellowship" : "residency";
    const trainingArr = getItemsByOrchidProType(
      data[trainingType],
      orchidProTypeId,
    );
    const remained = getItemsNotCurrOrchidProType(
      data[trainingType],
      orchidProTypeId,
    );
    const inputField = event.currentTarget;

    const training = getFinalTrainingArr(type, trainingArr, orchidProTypeId);

    training[0] = {
      ...training[0],
      [inputField.name]: inputField.value,
    };

    fieldChangedCallback(trainingType, remained.concat(training));
  };

  const addDegree = (degreeType, orchidProTypeId) => {
    const degreesArr = getItemsByOrchidProType(
      data[degreeType],
      orchidProTypeId,
    );
    const remained = getItemsNotCurrOrchidProType(
      data[degreeType],
      orchidProTypeId,
    );

    const degrees = getFinalDegreesArr(degreeType, degreesArr, orchidProTypeId);

    const newDegree = {
      type: degreeType,
      conferring_entity: null,
      degree: null,
      year: null,
      orchid_pro_type_id: orchidProTypeId,
    };
    fieldChangedCallback(
      degreeType,
      remained.concat(degrees.concat(newDegree)),
    );
  };

  const addTraining = (trainingType, orchidProTypeId) => {
    const type =
      trainingType === "fellowships_json" ? "fellowship" : "residency";
    const training = getItemsByOrchidProType(
      data[trainingType],
      orchidProTypeId,
    );
    const remained = getItemsNotCurrOrchidProType(
      data[trainingType],
      orchidProTypeId,
    );

    const newTraining = {
      type: type,
      conferring_entity: null,
      program: null,
      specialty: null,
      subspecialty: null,
      year: null,
      orchid_pro_type_id: orchidProTypeId,
    };

    fieldChangedCallback(
      trainingType,
      remained.concat(training.concat(newTraining)),
    );
  };

  const removeDegree = (degreeType, orchidProTypeId) => {
    const degrees = getItemsByOrchidProType(data[degreeType], orchidProTypeId);

    if (degreeType === "undergraduate") {
      degrees.splice(undergradIndexToDelete, 1);
    } else if (degreeType === "graduate") {
      degrees.splice(graduateIndexToDelete, 1);
    }

    const remained = getItemsNotCurrOrchidProType(
      data[degreeType],
      orchidProTypeId,
    );
    fieldChangedCallback(degreeType, remained.concat(degrees));
  };

  const onDegreeSelectChange =
    (degreeType, fieldName, orchidProTypeId, index) => (e) => {
      const degreesArr = getItemsByOrchidProType(
        data[degreeType],
        orchidProTypeId,
      );
      const remained = getItemsNotCurrOrchidProType(
        data[degreeType],
        orchidProTypeId,
      );

      const degrees = getFinalDegreesArr(
        degreeType,
        degreesArr,
        orchidProTypeId,
      );
      degrees[index] = {
        ...degrees[index],
        [fieldName]: e.value,
      };
      fieldChangedCallback(degreeType, remained.concat(degrees));
    };

  const onTrainingSelectChange =
    (trainingType, fieldName, orchidProTypeId) => (e) => {
      const key =
        trainingType === "fellowships" ? "fellowships_json" : "residency_json";
      const type = trainingType === "fellowships" ? "fellowship" : "residency";
      const trainingArr = getItemsByOrchidProType(data[key], orchidProTypeId);
      const remained = getItemsNotCurrOrchidProType(data[key], orchidProTypeId);

      const training = getFinalTrainingArr(type, trainingArr, orchidProTypeId);

      training[0] = {
        ...training[0],
        [fieldName]: e.value,
      };
      fieldChangedCallback(key, remained.concat(training));
    };

  return !data.orchid_pro_types?.length ||
    data.orchid_pro_types?.length === 0 ? null : (
    <div className={!registration ? styles["intake-form--edit-profile"] : ""}>
      {data.orchid_pro_types.map((item, index) => {
        const isTherapist = checkIsTherapist(item);
        const undergradArr = getItemsByOrchidProType(
          data.undergraduate,
          item.orchid_pro_type_id,
        );
        const graduateArr = getItemsByOrchidProType(
          data.graduate,
          item.orchid_pro_type_id,
        );

        const undergrad = getFinalDegreesArr(
          "undergraduate",
          undergradArr,
          item.orchid_pro_type_id,
        );
        const graduate = getFinalDegreesArr(
          "graduate",
          graduateArr,
          item.orchid_pro_type_id,
        );

        const residencyArr = getItemsByOrchidProType(
          data.residency_json,
          item.orchid_pro_type_id,
        );

        const fellowshipArr = getItemsByOrchidProType(
          data.fellowships_json,
          item.orchid_pro_type_id,
        );

        const residency = !isTherapist
          ? getFinalTrainingArr(
              "residency",
              residencyArr,
              item.orchid_pro_type_id,
            )
          : residencyArr;

        const fellowship = !isTherapist
          ? getFinalTrainingArr(
              "fellowship",
              fellowshipArr,
              item.orchid_pro_type_id,
            )
          : fellowshipArr;
        return (
          <div
            className={styles["intake-form__pro-type-block"]}
            key={item.orchid_pro_type_id || index}
          >
            {registration ? (
              <>
                <div className={styles["intake-form__i-am-a"]}>
                  I am a {getOrchidProTypeLabel(item.orchid_pro_type.type)}
                </div>
                <hr className={styles["intake-form__hr"]} />
              </>
            ) : (
              <div className={styles["intake-form__i-am-a--profile"]}>
                {getOrchidProTypeOrder(index)}-
                {getOrchidProTypeLabel(item.orchid_pro_type.type)}
              </div>
            )}

            <div className={styles["edit-profile__card"]}>
              {!registration && (
                <div
                  className={getClasses(
                    styles["intake-form__title"],
                    styles["intake-form__title--edit-profile"],
                  )}
                >
                  Education
                </div>
              )}
              <div className={styles["intake-form__title"]}>
                {!registration ? "" : "1. "}Provide your undergraduate
                Information
              </div>

              {/* confirm delete undergraduate degree */}
              <ConfirmModal
                labelTitle={messages.areYouSure}
                children={messages.doYouWantToDeleteThisSection}
                labelConfirm={messages.delete}
                hideCancel={true}
                show={undergradIndexToDelete !== undefined}
                onCancel={() => setUndergradIndexToDelete(undefined)}
                onConfirm={() => {
                  removeDegree("undergraduate", item.orchid_pro_type_id);
                  setUndergradIndexToDelete(undefined);
                }}
              />

              <Row className={styles["edit-profile__row"]}>
                {undergrad.map((undergradDegree, index) => {
                  return (
                    <Col
                      key={index}
                      md={registration ? 12 : 6}
                      lg={registration ? 12 : 6}
                      xs={12}
                      className={
                        registration
                          ? styles["education-training__degree-col"]
                          : styles["education-training__degree-col--profile"]
                      }
                    >
                      {registration && index >= 1 && (
                        <hr className={styles["education-training__hr"]} />
                      )}
                      <div
                        className={getClasses(
                          "d-flex justify-content-between",
                          undergrad.length > 1 && index >= 0 && "mb-3",
                        )}
                      >
                        {undergrad.length > 1 && (
                          <p className={styles["education-training__text"]}>
                            Undergraduate Degree {index + 1}
                          </p>
                        )}
                        {!!index && (
                          <IconButton
                            variant="link"
                            onClick={() => setUndergradIndexToDelete(index)}
                          >
                            <OutlineCross className="gray02" />
                          </IconButton>
                        )}
                      </div>

                      <Form.Group>
                        <Form.Label
                          className={styles["intake-form__form-label"]}
                        >
                          Undergraduate Institution{" "}
                          {index === 0 && <span className="required">*</span>}
                        </Form.Label>
                        <Form.Control
                          required={index === 0}
                          type="text"
                          name="conferring_entity"
                          value={undergradDegree.conferring_entity || ""}
                          onChange={onDegreeInputChange(
                            "undergraduate",
                            item.orchid_pro_type_id,
                            index,
                          )}
                          placeholder={"Enter Undergraduate Institution"}
                          onFocus={() =>
                            track(
                              amplitudePageName,
                              "undergrad-institution-field",
                              "focus",
                            )
                          }
                        />
                      </Form.Group>

                      <Form.Group>
                        <Form.Label
                          className={styles["intake-form__form-label"]}
                        >
                          Undergraduate Degree{" "}
                          {index === 0 && <span className="required">*</span>}
                        </Form.Label>
                        <CustomSelect
                          required={index === 0}
                          isMulti={false}
                          value={
                            UNDERGRADUATE_DEGREES.find(
                              (option) =>
                                option.value === undergradDegree.degree,
                            ) || ""
                          }
                          onChange={onDegreeSelectChange(
                            "undergraduate",
                            "degree",
                            item.orchid_pro_type_id,
                            index,
                          )}
                          placeholder={"Select Degree"}
                          options={UNDERGRADUATE_DEGREES}
                          onClick={() =>
                            track(
                              amplitudePageName,
                              "undergrad-degree-dropdown",
                              "click",
                            )
                          }
                        />
                      </Form.Group>

                      <Form.Group>
                        <Form.Label
                          className={styles["intake-form__form-label"]}
                        >
                          Year of Graduation{" "}
                          {index === 0 && <span className="required">*</span>}
                        </Form.Label>
                        <CustomSelect
                          required={index === 0}
                          isMulti={false}
                          value={
                            YEAR_OPTIONS.find(
                              (option) => option.value === undergradDegree.year,
                            ) || ""
                          }
                          placeholder={"Select Graduation Year"}
                          onChange={onDegreeSelectChange(
                            "undergraduate",
                            "year",
                            item.orchid_pro_type_id,
                            index,
                          )}
                          options={YEAR_OPTIONS}
                          onClick={() =>
                            track(
                              amplitudePageName,
                              "undergrad-grad-year-dropdown",
                              "click",
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                  );
                })}
              </Row>
              <Button
                variant="link"
                className="mb-3 d-flex align-items-center p-0"
                onClick={() => {
                  addDegree("undergraduate", item.orchid_pro_type_id);
                  track(
                    amplitudePageName,
                    "add-undergraduate-degree-button",
                    "click",
                  );
                }}
              >
                <OutlinePlus16 className="mr-1" /> Add Undergraduate Degree
              </Button>
              <hr className={styles["edit-profile__hr"]} />
              <div
                className={getClasses(
                  styles["intake-form__extra-spacing"],
                  styles["intake-form__title"],
                )}
              >
                {!registration ? "" : "2. "}Provide your graduate information
              </div>
              {/* confirm delete graduate degree */}
              <ConfirmModal
                labelTitle={messages.areYouSure}
                children={messages.doYouWantToDeleteThisSection}
                labelConfirm={messages.delete}
                hideCancel={true}
                show={graduateIndexToDelete !== undefined}
                onCancel={() => setGraduateIndexToDelete(undefined)}
                onConfirm={() => {
                  removeDegree("graduate", item.orchid_pro_type_id);
                  setGraduateIndexToDelete(undefined);
                }}
              />

              <Row className={styles["edit-profile__row"]}>
                {graduate.map((graduateDegree, index) => {
                  return (
                    <Col
                      key={index}
                      md={registration ? 12 : 6}
                      lg={registration ? 12 : 6}
                      xs={12}
                      className={
                        registration
                          ? styles["education-training__degree-col"]
                          : styles["education-training__degree-col--profile"]
                      }
                    >
                      {registration && index >= 1 && (
                        <hr className={styles["education-training__hr"]} />
                      )}
                      <div
                        className={getClasses(
                          "d-flex justify-content-between",
                          graduate.length > 1 && index >= 0 && "mb-3",
                        )}
                      >
                        {graduate.length > 1 && (
                          <p className={styles["education-training__text"]}>
                            Graduate Degree {index + 1}
                          </p>
                        )}
                        {!!index && (
                          <IconButton
                            variant="link"
                            onClick={() => setGraduateIndexToDelete(index)}
                          >
                            <OutlineCross className="gray02" />
                          </IconButton>
                        )}
                      </div>

                      <Form.Group>
                        <Form.Label
                          className={styles["intake-form__form-label"]}
                        >
                          Graduate Institution{" "}
                          {index === 0 && <span className="required">*</span>}
                        </Form.Label>

                        <Form.Control
                          required={index === 0}
                          type="text"
                          name="conferring_entity"
                          value={graduateDegree.conferring_entity || ""}
                          onChange={onDegreeInputChange(
                            "graduate",
                            item.orchid_pro_type_id,
                            index,
                          )}
                          placeholder={"Enter Graduate Institution"}
                          onFocus={() =>
                            track(
                              amplitudePageName,
                              "graduate-institution-field",
                              "focus",
                            )
                          }
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label
                          className={styles["intake-form__form-label"]}
                        >
                          Graduate Degree{" "}
                          {index === 0 && <span className="required">*</span>}
                        </Form.Label>
                        <CustomSelect
                          required={index === 0}
                          isMulti={false}
                          value={
                            NON_PHYSICIAN_DEGREE_OPTIONS.find(
                              (option) =>
                                option.value === graduateDegree.degree,
                            ) || ""
                          }
                          onChange={onDegreeSelectChange(
                            "graduate",
                            "degree",
                            item.orchid_pro_type_id,
                            index,
                          )}
                          placeholder={"Select Degree"}
                          options={NON_PHYSICIAN_DEGREE_OPTIONS}
                          onClick={() =>
                            track(
                              amplitudePageName,
                              "graduate-degree-dropdown",
                              "click",
                            )
                          }
                        />
                      </Form.Group>

                      <Form.Group>
                        <Form.Label
                          className={styles["intake-form__form-label"]}
                        >
                          Year of Graduation{" "}
                          {index === 0 && <span className="required">*</span>}
                        </Form.Label>
                        <CustomSelect
                          required={index === 0}
                          isMulti={false}
                          value={
                            YEAR_OPTIONS.find(
                              (option) => option.value === graduateDegree.year,
                            ) || ""
                          }
                          placeholder={"Select Graduation Year"}
                          onChange={onDegreeSelectChange(
                            "graduate",
                            "year",
                            item.orchid_pro_type_id,
                            index,
                          )}
                          options={YEAR_OPTIONS}
                          onClick={() =>
                            track(
                              amplitudePageName,
                              "graduate-institution-grad-year-dropdown",
                              "click",
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                  );
                })}
              </Row>

              <Button
                variant="link"
                className="mb-3 d-flex align-items-center p-0"
                onClick={() => {
                  addDegree("graduate", item.orchid_pro_type_id);
                  track(
                    amplitudePageName,
                    "add-graduate-degree-button",
                    "click",
                  );
                }}
              >
                <OutlinePlus16 className="mr-1" /> Add Graduate Degree
              </Button>
            </div>
            <div className={styles["edit-profile__card"]}>
              {!registration && (
                <div
                  className={getClasses(
                    styles["intake-form__title"],
                    styles["intake-form__title--edit-profile"],
                  )}
                >
                  Training
                </div>
              )}

              <Row className={styles["edit-profile__row"]}>
                <Col
                  md={registration ? 12 : 6}
                  lg={registration ? 12 : 6}
                  xs={12}
                  className={registration ? "p-0" : "pl-0"}
                >
                  <div
                    className={
                      registration ? styles["intake-form__extra-spacing"] : ""
                    }
                  >
                    {isTherapist && residency?.length < 1 && (
                      <Button
                        variant="link"
                        className="mb-3 d-flex align-items-center p-0"
                        onClick={() => {
                          addTraining(
                            "residency_json",
                            item.orchid_pro_type_id,
                          );
                          track(
                            amplitudePageName,
                            "add-residency-info-button",
                            "click",
                          );
                        }}
                      >
                        <OutlinePlus16 className="mr-1" /> Add Residency
                        Information
                      </Button>
                    )}

                    {residency?.map((residencyItem, index) => {
                      return (
                        <div key={index}>
                          <div className={styles["intake-form__title"]}>
                            {!registration ? "" : "3. "}Provide your Residency
                            Information
                          </div>
                          <Checkbox
                            label={
                              <label className="rg-14 mb-3">
                                I'm currently a resident
                              </label>
                            }
                            id={`curr-resident_${item.orchid_pro_type_id}`}
                            // onChange={}
                            defaultChecked={
                              residencyItem.year > new Date().getFullYear()
                            }
                            onClick={() =>
                              track(
                                amplitudePageName,
                                "currently-a-resident-radio",
                                "click",
                              )
                            }
                          />
                          <Form.Group>
                            <Form.Label
                              className={styles["intake-form__form-label"]}
                            >
                              Residency Institution
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="conferring_entity"
                              value={residencyItem.conferring_entity || ""}
                              onChange={onTrainingInputChange(
                                "residency_json",
                                item.orchid_pro_type_id,
                              )}
                              placeholder={"Enter Residency Institution"}
                              onFocus={() =>
                                track(
                                  amplitudePageName,
                                  "residency-institution-field",
                                  "focus",
                                )
                              }
                            />
                          </Form.Group>
                          <Form.Group>
                            <Form.Label
                              className={styles["intake-form__form-label"]}
                            >
                              Program Name
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="program"
                              value={residencyItem.program || ""}
                              onChange={onTrainingInputChange(
                                "residency_json",
                                item.orchid_pro_type_id,
                              )}
                              placeholder={"Enter Program Name"}
                              onFocus={() =>
                                track(
                                  amplitudePageName,
                                  "residency-program-name-field",
                                  "focus",
                                )
                              }
                            />
                          </Form.Group>

                          <Form.Group>
                            <Form.Label
                              className={styles["intake-form__form-label"]}
                            >
                              Specialty
                            </Form.Label>
                            <CustomSelect
                              isMulti={false}
                              onChange={onTrainingSelectChange(
                                "residency",
                                "specialty",
                                item.orchid_pro_type_id,
                              )}
                              options={PHYSICIAN_PRIMARY_SPECIALTY}
                              value={
                                PHYSICIAN_PRIMARY_SPECIALTY.find(
                                  (option) =>
                                    option.value ===
                                    residencyItem.specialty?.toLowerCase(),
                                ) || ""
                              }
                              onClick={() =>
                                track(
                                  amplitudePageName,
                                  "residency-specialty-dropdown",
                                  "click",
                                )
                              }
                            />
                          </Form.Group>

                          <Form.Group>
                            <Form.Label
                              className={styles["intake-form__form-label"]}
                            >
                              Sub-Specialty
                            </Form.Label>
                            <CustomSelect
                              isMulti={false}
                              onChange={onTrainingSelectChange(
                                "residency",
                                "subspecialty",
                                item.orchid_pro_type_id,
                              )}
                              options={PSYCHIATRY_SUBSPECIALTY_OPTIONS}
                              placeholder={"Select Sub-Specialty"}
                              value={
                                PSYCHIATRY_SUBSPECIALTY_OPTIONS.find(
                                  (option) =>
                                    option.value ===
                                    residencyItem.subspecialty?.toLowerCase(),
                                ) || ""
                              }
                              onClick={() =>
                                track(
                                  amplitudePageName,
                                  "residency-subspecialty-dropdown",
                                  "click",
                                )
                              }
                            />
                          </Form.Group>
                          <Form.Group>
                            <Form.Label
                              className={styles["intake-form__form-label"]}
                            >
                              Year of completion/Expected year of completion
                            </Form.Label>
                            <CustomSelect
                              isMulti={false}
                              value={
                                YEAR_OPTIONS.find(
                                  (option) =>
                                    option.value === residencyItem.year,
                                ) || ""
                              }
                              placeholder={"Enter Year"}
                              onChange={onTrainingSelectChange(
                                "residency",
                                "year",
                                item.orchid_pro_type_id,
                              )}
                              options={YEAR_OPTIONS}
                              onClick={() =>
                                track(
                                  amplitudePageName,
                                  "residency-completion-year-dropdown",
                                  "click",
                                )
                              }
                            />
                          </Form.Group>
                        </div>
                      );
                    })}
                  </div>
                </Col>
                <Col
                  md={registration ? 12 : 6}
                  lg={registration ? 12 : 6}
                  xs={12}
                  className={registration ? "p-0" : "pr-0"}
                >
                  <div
                    className={
                      registration ? styles["intake-form__extra-spacing"] : ""
                    }
                  >
                    {isTherapist && fellowship?.length < 1 && (
                      <Button
                        variant="link"
                        className="mb-3 d-flex align-items-center p-0"
                        onClick={() => {
                          track(
                            amplitudePageName,
                            "add-fellowship-button",
                            "click",
                          );
                          addTraining(
                            "fellowships_json",
                            item.orchid_pro_type_id,
                          );
                        }}
                      >
                        <OutlinePlus16 className="mr-1" /> Add Fellowship
                        Information
                      </Button>
                    )}

                    {fellowship?.map((fellowshipItem, index) => {
                      return (
                        <div key={index}>
                          <div className={styles["intake-form__title"]}>
                            {!registration ? "" : "4. "}Provide your Fellowship
                            Information
                          </div>
                          <Checkbox
                            label={
                              <label className="rg-14 mb-3">
                                I'm currently a fellow
                              </label>
                            }
                            id={`curr-fellow_${item.orchid_pro_type_id}`}
                            // onChange={}
                            defaultChecked={
                              fellowshipItem.year > new Date().getFullYear()
                            }
                            onClick={() =>
                              track(
                                amplitudePageName,
                                "currently-a-fellow-checkbox",
                                "click",
                              )
                            }
                          />
                          <Form.Group>
                            <Form.Label
                              className={styles["intake-form__form-label"]}
                            >
                              Fellowship Institution
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="conferring_entity"
                              value={fellowshipItem.conferring_entity || ""}
                              onChange={onTrainingInputChange(
                                "fellowships_json",
                                item.orchid_pro_type_id,
                              )}
                              placeholder={"Enter Fellowship Institution"}
                              onFocus={() =>
                                track(
                                  amplitudePageName,
                                  "fellowship-institution-field",
                                  "focus",
                                )
                              }
                            />
                          </Form.Group>
                          <Form.Group>
                            <Form.Label
                              className={styles["intake-form__form-label"]}
                            >
                              Program Name
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="program"
                              value={fellowshipItem.program || ""}
                              onChange={onTrainingInputChange(
                                "fellowships_json",
                                item.orchid_pro_type_id,
                              )}
                              placeholder={"Enter Program Name"}
                              onFocus={() =>
                                track(
                                  amplitudePageName,
                                  "fellowship-program-name-field",
                                  "focus",
                                )
                              }
                            />
                          </Form.Group>
                          <Form.Group>
                            <Form.Label
                              className={styles["intake-form__form-label"]}
                            >
                              Specialty
                            </Form.Label>
                            <CustomSelect
                              isMulti={false}
                              onChange={onTrainingSelectChange(
                                "fellowships",
                                "specialty",
                                item.orchid_pro_type_id,
                              )}
                              options={PHYSICIAN_PRIMARY_SPECIALTY}
                              value={
                                PHYSICIAN_PRIMARY_SPECIALTY.find(
                                  (option) =>
                                    option.value ===
                                    fellowshipItem.specialty?.toLowerCase(),
                                ) || ""
                              }
                              onClick={() =>
                                track(
                                  amplitudePageName,
                                  "fellowship-specialty-dropdown",
                                  "click",
                                )
                              }
                            />
                          </Form.Group>
                          <Form.Group>
                            <Form.Label
                              className={styles["intake-form__form-label"]}
                            >
                              Sub-Specialty
                            </Form.Label>
                            <CustomSelect
                              isMulti={false}
                              onChange={onTrainingSelectChange(
                                "fellowships",
                                "subspecialty",
                                item.orchid_pro_type_id,
                              )}
                              options={PSYCHIATRY_SUBSPECIALTY_OPTIONS}
                              placeholder={"Select Sub-Specialty"}
                              value={
                                PSYCHIATRY_SUBSPECIALTY_OPTIONS.find(
                                  (option) =>
                                    option.value ===
                                    fellowshipItem.subspecialty?.toLowerCase(),
                                ) || ""
                              }
                              onFocus={() =>
                                track(
                                  amplitudePageName,
                                  "fellowship-subspecialty-field",
                                  "focus",
                                )
                              }
                            />
                          </Form.Group>

                          <Form.Group>
                            <Form.Label
                              className={styles["intake-form__form-label"]}
                            >
                              Year of completion/Expected year of completion
                            </Form.Label>
                            <CustomSelect
                              isMulti={false}
                              value={
                                YEAR_OPTIONS.find(
                                  (option) =>
                                    option.value === fellowshipItem.year,
                                ) || ""
                              }
                              placeholder={"Enter Year"}
                              onChange={onTrainingSelectChange(
                                "fellowships",
                                "year",
                                item.orchid_pro_type_id,
                              )}
                              options={YEAR_OPTIONS}
                              onClick={() =>
                                track(
                                  amplitudePageName,
                                  "fellowship-year-of-completion-dropdown",
                                  "click",
                                )
                              }
                            />
                          </Form.Group>
                        </div>
                      );
                    })}
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EducationTraining;
